/**
 * Which way a toolbar menu opens. Menus hang from their button, and the dock
 * panel they're in clips anything past its edge (a menu opening to the right
 * near a panel's right edge disappears under the next panel). A menu opens
 * from the button's left edge when it fits, and from its right edge when that
 * fits instead, as desktop menus flip at a screen edge.
 */

export interface HorizontalSpan {
  readonly left: number;
  readonly right: number;
}

export type MenuAlignment = "start" | "end";

/** `bounds` is the clipping container's span; all in the same (viewport) pixels. */
export function menuAlignment(
  button: HorizontalSpan,
  menuWidth: number,
  bounds: HorizontalSpan,
): MenuAlignment {
  const fitsAtStart = button.left + menuWidth <= bounds.right;
  const fitsAtEnd = button.right - menuWidth >= bounds.left;
  return !fitsAtStart && fitsAtEnd ? "end" : "start";
}

/** The span of the nearest ancestor that clips its overflow, else the window. */
export function clippingSpan(element: Element): HorizontalSpan {
  for (let at = element.parentElement; at; at = at.parentElement) {
    const { overflowX } = getComputedStyle(at);
    if (overflowX !== "visible") {
      const rect = at.getBoundingClientRect();
      return { left: rect.left, right: rect.right };
    }
  }
  return { left: 0, right: window.innerWidth };
}
